import { type FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DragDropContext } from 'react-beautiful-dnd';
import { Row, Container, Button, Form } from 'react-bootstrap';
import { BsArrowsCollapse, BsArrowsExpand, BsFileEarmarkPlus } from 'react-icons/bs';
import { debounce } from 'lodash-es';

import {
	movePage,
	movePageGroup,
	moveChapter,
	editChaptersCollapseState,
	updateCourseResource
} from '@editor2/reducers/editor/editorSlice';
import { type Entity } from '@editor2/types/entity';
import { type Chapter } from '@editor2/types/chapter';
import { type Book } from '@editor2/types/book';
import { splitToChapterGroups } from '@editor2/services/helpersService';
import { CollapseState } from '@editor2/types/collapseState';
import AddChapterModal from '@editor2/components/AddChapterModal';
import TableOfContentsColumn from './TocColumn';
import { tocBlock, collapseTools } from '@editor2/containers/TableOfContentsPage/styles';

interface Props {
	book: Book;
	chapters: Entity<Chapter>;
	courseId: number;
}

const TableOfContents: FC<Props> = (props) => {
	const { book, chapters, courseId } = props;

	const dispatch = useDispatch();
	const [isAddChapterModalShown, setAddChapterModalShown] = useState(false);

	const [editorialNotes, setEditorialNotes] = useState(book.editorial_notes);

	const { chapterGroups, groupSize } = chapters
		? splitToChapterGroups(chapters.ids, 3)
		: { chapterGroups: [], groupSize: 0 };

	useEffect(() => {
		if (editorialNotes !== book.editorial_notes) setEditorialNotes(book.editorial_notes);
	}, [book.editorial_notes]);

	const onDragEnd = (result) => {
		const { source, destination, draggableId, type } = result;
		const { droppableId: sourceDroppableId } = source;
		const { droppableId: destinationDroppableId, index: destinationIndex } = destination;

		if (type === 'Chapter') {
			dispatch(moveChapter({ chapterId: draggableId, index: destinationIndex, courseId }));
		}

		if (type === 'PageGroup') {
			dispatch(
				movePageGroup({
					sourceDroppableId,
					destinationDroppableId,
					destinationIndex,
					pageGroupId: draggableId,
					courseId
				})
			);
		}

		if (type === 'Page') {
			dispatch(
				movePage({
					sourceDroppableId,
					destinationDroppableId,
					destinationIndex,
					pageId: draggableId,
					courseId
				})
			);
		}
	};

	const onExpandAll = () => {
		dispatch(editChaptersCollapseState({ collapseState: CollapseState.ExpandAll, courseId }));
	};

	const onCollapseAll = () => {
		dispatch(editChaptersCollapseState({ collapseState: CollapseState.CollapseAll, courseId }));
	};

	const debouncedDispatch = useCallback(
		debounce((action) => {
			dispatch(action);
		}, 500),
		[]
	);

	const onChange = (value) => {
		setEditorialNotes(value.editorial_notes);
		debouncedDispatch(updateCourseResource({ ...value, courseId }));
	};

	return (
		<div className={tocBlock}>
			<Form.Group>
				<Form.Label>Editorial notes</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					value={editorialNotes}
					onChange={(e) => onChange({ editorial_notes: e.target.value })}
				/>
			</Form.Group>

			<div className={collapseTools}>
				<Button variant="outline-secondary" onClick={() => setAddChapterModalShown(true)}>
					<BsFileEarmarkPlus />
					Add chapter
				</Button>
				<Button variant="outline-secondary" onClick={onExpandAll}>
					<BsArrowsExpand />
					Expand All
				</Button>
				<Button variant="outline-secondary" onClick={onCollapseAll}>
					<BsArrowsCollapse />
					Collapse All
				</Button>
			</div>

			<DragDropContext onDragEnd={onDragEnd}>
				<Container fluid>
					<Row>
						{chapterGroups.map((chapterIds, i) => (
							<TableOfContentsColumn
								chapterIds={chapterIds}
								courseId={courseId}
								columnIndex={i}
								groupSize={groupSize}
								key={i}
							/>
						))}
					</Row>
				</Container>
			</DragDropContext>

			{isAddChapterModalShown && (
				<AddChapterModal
					onHide={() => setAddChapterModalShown(false)}
					isShown={isAddChapterModalShown}
					courseId={courseId}
				/>
			)}
		</div>
	);
};

export default TableOfContents;
